import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import Theme from 'components/common/Theme/Theme'
import Media from 'components/common/Media'
import Mixin from 'components/common/Mixin'
import Icon from 'components/common/Icon/Icon'
import { A } from 'components/common/Link/Link'
import { Map, MapMarker } from 'components/common/GoogleMaps'
import { formatPhoneNumber } from 'helpers/phone-number'
import { activateMarchexScript } from 'helpers/custom-events'

import { Headline } from '../Common.styles'

const DEFAULT_SECTION_ICON = 'location-red'
const DEFAULT_SECTION_TITLE = 'Your Store'
const DEFAULT_ZOOM = 15

const YourStoreFieldComponent = ({
  className,
  icon = DEFAULT_SECTION_ICON,
  title = DEFAULT_SECTION_TITLE,
  hasStore,
  myStore: {
    distanceRadius,
    address: {
      address1,
      city,
      state,
      zipcode
    } = {},
    phoneNumbers: [
      phone
    ] = [],
    ...store
  },
  mapConfig,
  showStoreLocatorModalInAppointmentMode,
  handleTAOSDateTimeUpdate,
  targetId,
  ...props
}) =>
  <StoreSection className={ className } { ...props }>
    <Headline data-at-your-store-headline left asset={ icon } title={ title } targetId={ targetId } />
    {
      hasStore &&
        <MapWrapper data-at-your-store-map>
          <Map center={ mapConfig } defaultZoom={ DEFAULT_ZOOM }>
            <MapMarker position={ mapConfig } />
          </Map>
        </MapWrapper>
    }
    <Block>
      {
        hasStore ?
          <Fragment>
            <Media.Desktop>
              <Distance data-at-your-store-distance>{ distanceRadius } miles</Distance>
              <Name data-at-your-store-address1>{ address1 }</Name>
              <Name data-at-your-store-address2>{ city }, { state }, { zipcode }</Name>
              <Name data-at-your-store-phone>{ formatPhoneNumber(phone) }</Name>
            </Media.Desktop>
            <Media.Mobile>
              <Wrapper>
                <Active asset="check-circle" />
                <Name data-at-your-store-address1>{ address1 }</Name>
                <Name data-at-your-store-address2>{ city }, { state }, { zipcode }</Name>
                <Icon asset="location-red" margin="0 2px 0 0" alt="" />
                <Distance data-at-your-store-distance>{ distanceRadius } miles</Distance>
              </Wrapper>
            </Media.Mobile>
            <StoreBlock>
              <A
                data-at-your-store-link-change-location
                prevent
                bolder
                handler={ () => {
                  showStoreLocatorModalInAppointmentMode(handleTAOSDateTimeUpdate)
                  activateMarchexScript()
                } }>Change Location</A>
            </StoreBlock>
          </Fragment> :
          <StoreBlock hasMargin>
            <A
              data-at-your-store-link-select-a-store
              prevent
              bolder
              handler={ () => showStoreLocatorModalInAppointmentMode(handleTAOSDateTimeUpdate) }>Select a store</A>
          </StoreBlock>
      }
    </Block>
  </StoreSection>

YourStoreFieldComponent.propTypes = {
  className: PropTypes.string,
  handleTAOSDateTimeUpdate: PropTypes.func,
  hasStore: PropTypes.bool,
  icon: PropTypes.string,
  mapConfig: PropTypes.shape(),
  myStore: PropTypes.oneOfType([
    PropTypes.shape(),
    PropTypes.any
  ]),
  selected: PropTypes.bool,
  showStoreLocatorModalInAppointmentMode: PropTypes.func,
  targetId: PropTypes.string,
  title: PropTypes.string
}

export default YourStoreFieldComponent

const StoreSection = styled.div`
  ${Media.desktop`
    display: inline-block;
    width: 50%;
    margin-bottom: 50px;
    vertical-align: top;
  `}
  ${Mixin.responsive('padding', '10px 0 0 23px', '33px 0 30px')}
`

const Block = styled.div`
  ${Media.desktop`
    display: inline-block;
    padding-left: 20px;
    vertical-align: middle;
  `}
`

const Distance = styled.div`
  color: ${Theme.colors.sub_grey};
  ${Mixin.font.GothamBook(14, 400, 19)}
  ${Media.mobile`
    display: inline-block;
    vertical-align: middle;
  `}
`

const Name = styled.div`
  color: ${Theme.colors.black};
  ${Mixin.font.GothamBold(18, 600, 24)}
  ${Media.mobile`
    & + & {
      margin: 0 0 10px;
    }
  `}
  ${Media.desktop`
    &:last-child {
      margin: 0 0 10px;
    }
  `}
  ${Media.tablet`
    font-size: 14px;
  `}
`

const StoreBlock = styled.div`
  ${Media.mobile`
    padding-top: 20px;
    text-align: center;
  `}
  ${props => props.hasMargin && css`
    margin-top: 40px;
    margin-left: 40px;
  `}
`

const Wrapper = styled.div`
  position: relative;
  padding: 15px 20px;
  border: 2px solid ${Theme.colors.dark_grey};
`

const Active = styled(Icon)`
  position: absolute;
  top: 10px;
  right: 10px;
`

const MapWrapper = styled.div`
  position: relative;
  display: inline-block;
  vertical-align: middle;
  height: 102px;
  width: 180px;
  ${Mixin.desktopOnly()}
`
